/**
 * Phase 12.8 — the profile / avatar area load placeholder.
 *
 * `UI_UX_STANDARDS.md` ("Required Skeleton Components") lists `SkeletonProfile`
 * as a component that must exist **before** the corresponding feature screen is
 * implemented. It did not exist; the Profile page is the first screen to need
 * it, so it is built here rather than improvised inside the page.
 *
 * Shape follows the existing skeletons exactly: the shared `.skeleton-shimmer`
 * class (defined once in `index.css`, and disabled under
 * `prefers-reduced-motion`), plain divs, no props beyond what varies, and no
 * animation of its own.
 *
 * ── Accessibility ─────────────────────────────────────────────────────────
 * A skeleton is decorative: it conveys "not ready yet", not content. It is
 * therefore marked `role="status"` with an `aria-label`, so a screen reader
 * announces the wait once, while the shimmer boxes themselves are hidden with
 * `aria-hidden` — otherwise a reader would walk a dozen empty elements.
 */
interface SkeletonProfileProps {
  /** Rows in the account-details block. Defaults to the six the page renders. */
  rows?: number;
}

export function SkeletonProfile({ rows = 6 }: SkeletonProfileProps) {
  return (
    <div className="space-y-5" role="status" aria-label="Loading profile">
      {/* Identity card — avatar, name, email, role */}
      <div
        className="flex items-center gap-4 rounded-xl border border-border bg-card p-5"
        aria-hidden="true"
      >
        <div className="skeleton-shimmer h-16 w-16 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="skeleton-shimmer h-5 w-40 rounded" />
          <div className="skeleton-shimmer h-4 w-56 rounded" />
          <div className="skeleton-shimmer h-4 w-32 rounded" />
        </div>
      </div>

      {/* Account details */}
      <div className="rounded-xl border border-border bg-card p-5" aria-hidden="true">
        <div className="skeleton-shimmer mb-4 h-4 w-32 rounded" />
        <div className="space-y-3">
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="skeleton-shimmer h-3.5 w-3.5 shrink-0 rounded" />
              <div className="space-y-1.5">
                <div className="skeleton-shimmer h-3 w-24 rounded" />
                <div className="skeleton-shimmer h-4 w-40 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
